import React, { useState, useEffect } from 'react';
import {
  FiX, FiActivity, FiAlertCircle, FiAlertTriangle, FiCheckCircle,
  FiInfo, FiRefreshCw, FiArrowRight, FiCloud, FiSliders,
} from 'react-icons/fi';
import {
  detectArchitectureDrift,
  type DriftReport, type DriftAction,
} from '../../api/ai';
import { getConnections, type ConnectionService } from '../../api/connections';

interface Props {
  isOpen: boolean;
  onClose: () => void;
  nodes: any[];
  edges: any[];
  provider: string;
  environment: string;
  architectureName?: string;
}

const STATUS_CONFIG = {
  in_sync:       { label: 'Synchronisé',    badge: 'bg-green-100 text-green-700 border-green-300',   ring: '#22c55e' },
  minor_drift:   { label: 'Drift mineur',   badge: 'bg-yellow-100 text-yellow-700 border-yellow-300', ring: '#f59e0b' },
  major_drift:   { label: 'Drift majeur',   badge: 'bg-orange-100 text-orange-700 border-orange-300', ring: '#f97316' },
  critical_drift:{ label: 'Drift critique', badge: 'bg-red-100 text-red-700 border-red-300',          ring: '#ef4444' },
};

const SEVERITY_CONFIG = {
  critical: { label: 'Critique',  bg: 'bg-red-100',    text: 'text-red-700',    border: 'border-red-300',    icon: <FiAlertCircle size={13} className="text-red-600" /> },
  high:     { label: 'Élevé',     bg: 'bg-orange-100', text: 'text-orange-700', border: 'border-orange-300', icon: <FiAlertTriangle size={13} className="text-orange-600" /> },
  medium:   { label: 'Moyen',     bg: 'bg-yellow-100', text: 'text-yellow-700', border: 'border-yellow-300', icon: <FiInfo size={13} className="text-yellow-600" /> },
  low:      { label: 'Faible',    bg: 'bg-blue-100',   text: 'text-blue-700',   border: 'border-blue-300',   icon: <FiInfo size={13} className="text-blue-500" /> },
} as const;

const ACTION_TYPE_CONFIG = {
  deploy:   { label: 'Déployer',    color: 'text-green-600',  bg: 'bg-green-50 border-green-200' },
  document: { label: 'Documenter', color: 'text-blue-600',   bg: 'bg-blue-50 border-blue-200' },
  remove:   { label: 'Supprimer',   color: 'text-red-600',    bg: 'bg-red-50 border-red-200' },
  update:   { label: 'Mettre à jour', color: 'text-orange-600', bg: 'bg-orange-50 border-orange-200' },
};

function ScoreRing({ score }: { score: number }) {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const color = score >= 80 ? '#22c55e' : score >= 60 ? '#f59e0b' : score >= 40 ? '#f97316' : '#ef4444';
  const offset = circ - (score / 100) * circ;
  return (
    <div className="relative inline-flex items-center justify-center">
      <svg width={88} height={88}>
        <circle cx={44} cy={44} r={r} fill="none" stroke="#e5e7eb" strokeWidth={8} />
        <circle cx={44} cy={44} r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={circ} strokeDashoffset={offset}
          strokeLinecap="round" transform="rotate(-90 44 44)" />
      </svg>
      <span className="absolute text-xl font-bold" style={{ color }}>{score}</span>
    </div>
  );
}

const DriftDetectionModal: React.FC<Props> = ({
  isOpen, onClose, nodes, edges, provider, environment, architectureName,
}) => {
  const [step, setStep]               = useState<'select' | 'result'>('select');
  const [connections, setConnections] = useState<ConnectionService[]>([]);
  const [selectedConn, setSelected]   = useState<string>('');
  const [loading, setLoading]         = useState(false);
  const [connLoading, setConnLoading] = useState(false);
  const [error, setError]             = useState<string | null>(null);
  const [result, setResult]           = useState<Awaited<ReturnType<typeof detectArchitectureDrift>> | null>(null);
  const [activeTab, setActiveTab]     = useState<'missing' | 'undocumented' | 'config' | 'actions'>('missing');

  const providerName = { azure: 'Azure', aws: 'AWS', gcp: 'Google Cloud' }[provider] || provider;

  useEffect(() => {
    if (!isOpen) return;
    setStep('select');
    setResult(null);
    setError(null);
    setSelected('');
    setConnLoading(true);
    getConnections()
      .then(all => setConnections(all.filter(c => c.provider === provider && c.is_active)))
      .catch(() => setConnections([]))
      .finally(() => setConnLoading(false));
  }, [isOpen, provider]);

  const handleScan = async () => {
    if (!selectedConn) return;
    setLoading(true);
    setError(null);
    try {
      const data = await detectArchitectureDrift({
        nodes, edges, provider, environment, architectureName, connectionId: selectedConn,
      });
      setResult(data);
      setStep('result');
      setActiveTab('missing');
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Erreur lors de la détection de drift');
    } finally {
      setLoading(false);
    }
  };

  const handleExportPdf = () => {
    if (!result) return;
    const w = window.open('', '_blank');
    if (!w) return;
    const { driftReport: d, metadata } = result;
    const missingRows = d.missingInCloud.map(m =>
      `<tr><td>${m.canvasLabel}</td><td>${m.canvasType}</td><td class="${m.severity}">${m.severity}</td><td>${m.recommendation}</td></tr>`).join('');
    const undocRows = d.undocumentedInCloud.map(u =>
      `<tr><td>${u.cloudName}</td><td>${u.cloudType}</td><td class="${u.severity}">${u.severity}</td><td>${u.recommendation}</td></tr>`).join('');
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"/>
    <title>Drift Report — ${architectureName}</title>
    <style>
      body{font-family:system-ui,sans-serif;padding:32px;max-width:900px;margin:0 auto;font-size:14px;color:#111}
      h1{font-size:20px;font-weight:700;border-bottom:2px solid #6366f1;padding-bottom:8px}
      h2{font-size:16px;font-weight:600;margin:20px 0 8px;color:#312e81}
      table{width:100%;border-collapse:collapse;margin:12px 0}
      th{background:#eef2ff;padding:8px 12px;text-align:left;font-size:12px;border:1px solid #c7d2fe}
      td{padding:8px 12px;font-size:12px;border:1px solid #e0e7ff}
      .critical{color:#dc2626;font-weight:600}.high{color:#ea580c;font-weight:600}
      .medium{color:#d97706}.low{color:#2563eb}
      @media print{body{padding:16px}}
    </style></head><body>`);
    w.document.write(`<h1>Rapport de Drift — ${architectureName || providerName}</h1>`);
    w.document.write(`<p><b>Score :</b> ${d.driftScore}/100 | <b>Statut :</b> ${STATUS_CONFIG[d.status]?.label} | <b>Connexion :</b> ${metadata.connectionName} | <b>Ressources cloud :</b> ${result.cloudResourcesCount}</p>`);
    w.document.write(`<p>${d.summary}</p>`);
    if (d.missingInCloud.length) { w.document.write(`<h2>Manquant dans le cloud (${d.missingInCloud.length})</h2><table><tr><th>Label</th><th>Type</th><th>Sévérité</th><th>Recommandation</th></tr>${missingRows}</table>`); }
    if (d.undocumentedInCloud.length) { w.document.write(`<h2>Non documenté dans GenIaC (${d.undocumentedInCloud.length})</h2><table><tr><th>Nom</th><th>Type</th><th>Sévérité</th><th>Recommandation</th></tr>${undocRows}</table>`); }
    if (d.actions.length) { w.document.write(`<h2>Actions prioritaires</h2><ol>${d.actions.sort((a,b)=>a.priority-b.priority).map(a=>`<li><b>[${a.type}]</b> ${a.title} — ${a.description}</li>`).join('')}</ol>`); }
    w.document.write('</body></html>');
    w.document.close();
    setTimeout(() => w.print(), 400);
  };

  if (!isOpen) return null;

  const report = result?.driftReport;
  const statusCfg = report ? (STATUS_CONFIG[report.status] || STATUS_CONFIG.minor_drift) : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] flex flex-col">

        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <FiActivity className="text-indigo-600" size={20} />
            <h2 className="text-lg font-semibold text-gray-800">Drift Detection</h2>
            <span className="text-xs text-gray-400 font-normal">Canvas vs {providerName} réel</span>
          </div>
          <div className="flex items-center gap-2">
            {step === 'result' && (
              <button onClick={() => { setStep('select'); setResult(null); setError(null); }}
                className="text-xs text-gray-500 hover:text-indigo-600 transition-colors px-2 py-1 rounded hover:bg-gray-50">
                ← Relancer
              </button>
            )}
            <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-400 hover:text-gray-600 transition-colors">
              <FiX size={18} />
            </button>
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto p-6">

          {/* Step 1: Select connection */}
          {step === 'select' && (
            <div className="space-y-5 max-w-lg mx-auto">
              <p className="text-sm text-gray-600">
                Sélectionne une connexion <strong>{providerName}</strong> pour comparer l'architecture canvas avec les ressources réellement déployées.
              </p>

              {connLoading && (
                <div className="flex items-center gap-2 text-gray-500 text-sm">
                  <FiRefreshCw size={14} className="animate-spin" /> Chargement des connexions…
                </div>
              )}

              {!connLoading && connections.length === 0 && (
                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <p className="text-sm text-amber-700 font-medium mb-1">Aucune connexion {providerName} active</p>
                  <p className="text-xs text-amber-600">Configurez une connexion cloud dans Paramètres → Connexions.</p>
                </div>
              )}

              {connections.length > 0 && (
                <div className="space-y-2">
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">
                    <FiCloud size={12} className="inline mr-1" />Connexion {providerName}
                  </label>
                  {connections.map(conn => (
                    <button key={conn.id}
                      onClick={() => setSelected(conn.id)}
                      className={`w-full flex items-center gap-3 p-3 rounded-xl border transition-colors text-left ${
                        selectedConn === conn.id
                          ? 'border-indigo-400 bg-indigo-50'
                          : 'border-gray-200 hover:border-gray-300 bg-white'
                      }`}>
                      <FiCloud size={16} className={selectedConn === conn.id ? 'text-indigo-500' : 'text-gray-400'} />
                      <div>
                        <p className="text-sm font-medium text-gray-800">{conn.name}</p>
                        <p className="text-xs text-gray-500">
                          {conn.test_status === 'success'
                            ? <span className="text-green-600">✓ Testée</span>
                            : conn.test_status === 'failed'
                            ? <span className="text-red-500">✗ Échec test</span>
                            : 'Non testée'}
                          {conn.last_tested_at && ` · ${new Date(conn.last_tested_at).toLocaleDateString('fr-FR')}`}
                        </p>
                      </div>
                      {selectedConn === conn.id && <FiCheckCircle size={16} className="text-indigo-500 ml-auto" />}
                    </button>
                  ))}
                </div>
              )}

              <div className="bg-gray-50 border border-gray-200 rounded-xl p-3">
                <p className="text-xs text-gray-500 mb-1">Architecture analysée :</p>
                <p className="text-sm font-medium text-gray-700">{architectureName || 'Architecture sans nom'}</p>
                <p className="text-xs text-gray-500 mt-1">
                  {nodes.filter(n => n.data?.type).length} ressources · {providerName} · {environment}
                </p>
              </div>

              {error && (
                <div className="bg-red-50 border border-red-200 rounded-xl p-3 text-red-700 text-sm flex items-center gap-2">
                  <FiAlertCircle size={14} />{error}
                </div>
              )}

              <button
                onClick={handleScan}
                disabled={loading || !selectedConn}
                className="w-full py-3 rounded-xl text-sm font-semibold text-white flex items-center justify-center gap-2 transition-all disabled:opacity-50"
                style={{ background: loading || !selectedConn ? '#9ca3af' : 'linear-gradient(135deg, #4f46e5, #6366f1)' }}>
                {loading
                  ? <><FiRefreshCw size={15} className="animate-spin" /> Analyse en cours… (peut prendre 30s)</>
                  : <><FiActivity size={15} /> Lancer la détection de drift</>}
              </button>
            </div>
          )}

          {/* Step 2: Results */}
          {step === 'result' && report && result && (
            <div className="space-y-5">

              {/* Summary bar */}
              <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-xl p-4 flex flex-wrap gap-4">
                <div className="flex flex-col items-center gap-1">
                  <ScoreRing score={report.driftScore} />
                  <span className="text-xs text-gray-500">Score sync</span>
                </div>
                <div className="flex-1 min-w-[180px]">
                  <div className="flex items-center gap-2 mb-2">
                    {statusCfg && (
                      <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${statusCfg.badge}`}>
                        {statusCfg.label}
                      </span>
                    )}
                    <span className="text-xs text-gray-500">{result.cloudResourcesCount} ressources dans le cloud</span>
                  </div>
                  <p className="text-sm text-gray-700 mb-2">{report.summary}</p>
                  <div className="flex gap-3 text-xs">
                    <span className="text-red-600 font-medium">{report.summary_counts.missing} manquant{report.summary_counts.missing !== 1 ? 's' : ''}</span>
                    <span className="text-orange-500 font-medium">{report.summary_counts.undocumented} non documenté{report.summary_counts.undocumented !== 1 ? 's' : ''}</span>
                    <span className="text-yellow-600 font-medium">{report.summary_counts.config_drift} config drift</span>
                  </div>
                </div>
                <div className="border-l border-indigo-100 pl-4 text-xs text-gray-500 space-y-1">
                  <p>Connexion : <span className="font-medium text-gray-700">{result.metadata.connectionName}</span></p>
                  {result.metadata.resourceGroupName && (
                    <p>Scope RG : <span className="font-medium text-indigo-600">{result.metadata.resourceGroupName}</span></p>
                  )}
                  <p>Canvas : <span className="font-medium text-gray-700">{nodes.filter(n => n.data?.type).length} ressources</span></p>
                  <p>Cloud : <span className="font-medium text-gray-700">{result.cloudResourcesCount} ressources</span></p>
                  {result.localDiff && (
                    <p>Matchées : <span className="font-medium text-green-600">{result.localDiff.matched}</span></p>
                  )}
                  {result.fetchErrors?.length > 0 && (
                    <p className="text-orange-500">⚠ {result.fetchErrors.length} erreur(s) fetch</p>
                  )}
                </div>
              </div>

              {/* Tabs */}
              <div className="flex gap-1 border-b border-gray-200">
                {([
                  { key: 'missing',       label: `Manquant (${report.missingInCloud.length})`,       icon: <FiAlertCircle size={13} /> },
                  { key: 'undocumented',  label: `Non documenté (${report.undocumentedInCloud.length})`, icon: <FiCloud size={13} /> },
                  { key: 'config',        label: `Config drift (${report.configDrift.length})`,       icon: <FiSliders size={13} /> },
                  { key: 'actions',       label: `Actions (${report.actions.length})`,                icon: <FiArrowRight size={13} /> },
                ] as const).map(tab => (
                  <button key={tab.key}
                    onClick={() => setActiveTab(tab.key)}
                    className={`flex items-center gap-1.5 text-xs font-medium px-3 py-2.5 border-b-2 transition-colors whitespace-nowrap ${
                      activeTab === tab.key
                        ? 'border-indigo-500 text-indigo-600'
                        : 'border-transparent text-gray-500 hover:text-gray-700'
                    }`}>
                    {tab.icon}{tab.label}
                  </button>
                ))}
              </div>

              {/* Missing in cloud */}
              {activeTab === 'missing' && (
                <div className="space-y-2">
                  {report.missingInCloud.length === 0 && (
                    <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                      <FiCheckCircle size={16} /><span className="text-sm font-medium">Toutes les ressources canvas sont déployées !</span>
                    </div>
                  )}
                  {report.missingInCloud.map((m, i) => {
                    const cfg = SEVERITY_CONFIG[m.severity] ?? SEVERITY_CONFIG.low;
                    return (
                      <div key={i} className={`border rounded-lg p-3 ${cfg.border} ${cfg.bg}`}>
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5 shrink-0">{cfg.icon}</span>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded border ${cfg.bg} ${cfg.text} ${cfg.border}`}>{cfg.label}</span>
                              <span className="text-sm font-medium text-gray-800">{m.canvasLabel}</span>
                              <span className="text-xs font-mono text-gray-500">{m.canvasType}</span>
                            </div>
                            <p className="text-xs text-gray-700">→ {m.recommendation}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Undocumented in cloud */}
              {activeTab === 'undocumented' && (
                <div className="space-y-2">
                  {report.undocumentedInCloud.length === 0 && (
                    <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                      <FiCheckCircle size={16} /><span className="text-sm font-medium">Toutes les ressources cloud sont documentées dans GenIaC !</span>
                    </div>
                  )}
                  {report.undocumentedInCloud.map((u, i) => {
                    const cfg = SEVERITY_CONFIG[u.severity] ?? SEVERITY_CONFIG.low;
                    return (
                      <div key={i} className={`border rounded-lg p-3 ${cfg.border} ${cfg.bg}`}>
                        <div className="flex items-start gap-2">
                          <span className="mt-0.5 shrink-0">{cfg.icon}</span>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-semibold px-1.5 py-0.5 rounded border ${cfg.bg} ${cfg.text} ${cfg.border}`}>{cfg.label}</span>
                              <span className="text-sm font-medium text-gray-800">{u.cloudName}</span>
                              <span className="text-xs font-mono text-gray-500">{u.cloudType}</span>
                            </div>
                            <p className="text-xs text-gray-700">→ {u.recommendation}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Config drift */}
              {activeTab === 'config' && (
                <div className="space-y-2">
                  {report.configDrift.length === 0 && (
                    <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                      <FiCheckCircle size={16} /><span className="text-sm font-medium">Aucun écart de configuration détecté !</span>
                    </div>
                  )}
                  {report.configDrift.map((c, i) => (
                    <div key={i} className="border border-yellow-200 bg-yellow-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-2">
                        <FiSliders size={13} className="text-yellow-600" />
                        <span className="text-sm font-medium text-gray-800">{c.resourceName}</span>
                        <span className="text-xs font-mono text-gray-500">· {c.field}</span>
                      </div>
                      <div className="grid grid-cols-2 gap-2">
                        <div className="bg-red-50 border border-red-200 rounded p-2">
                          <p className="text-[10px] text-red-500 font-semibold mb-0.5">Canvas</p>
                          <p className="text-xs text-gray-700 font-mono">{c.canvasValue}</p>
                        </div>
                        <div className="bg-green-50 border border-green-200 rounded p-2">
                          <p className="text-[10px] text-green-600 font-semibold mb-0.5">Cloud réel</p>
                          <p className="text-xs text-gray-700 font-mono">{c.cloudValue}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Actions */}
              {activeTab === 'actions' && (
                <div className="space-y-2">
                  {report.actions.length === 0 && (
                    <div className="flex items-center gap-2 text-green-700 bg-green-50 border border-green-200 rounded-lg p-3">
                      <FiCheckCircle size={16} /><span className="text-sm font-medium">Aucune action requise !</span>
                    </div>
                  )}
                  {report.actions.sort((a, b) => a.priority - b.priority).map((action, i) => {
                    const cfg = ACTION_TYPE_CONFIG[action.type] || ACTION_TYPE_CONFIG.update;
                    return (
                      <div key={i} className={`border rounded-lg p-3 ${cfg.bg}`}>
                        <div className="flex items-start gap-2">
                          <span className={`text-xs font-bold w-5 h-5 rounded-full bg-white border flex items-center justify-center shrink-0 ${cfg.color}`}>
                            {action.priority}
                          </span>
                          <div>
                            <div className="flex items-center gap-2 mb-1">
                              <span className={`text-xs font-semibold ${cfg.color}`}>[{cfg.label}]</span>
                              <span className="text-sm font-medium text-gray-800">{action.title}</span>
                            </div>
                            <p className="text-xs text-gray-600">{action.description}</p>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        {step === 'result' && report && (
          <div className="border-t border-gray-100 px-6 py-3 flex items-center justify-between gap-4 bg-gray-50 rounded-b-2xl">
            <div className="text-xs text-gray-400 flex items-center gap-3">
              {result?.metadata && (
                <>
                  <span>{result.metadata.tokensUsed.toLocaleString()} tokens</span>
                  <span>{(result.metadata.durationMs / 1000).toFixed(1)}s</span>
                  <span>{new Date(result.metadata.generatedAt).toLocaleTimeString('fr-FR')}</span>
                </>
              )}
            </div>
            <button onClick={handleExportPdf}
              className="flex items-center gap-1.5 text-xs text-gray-600 hover:text-indigo-600 transition-colors px-2.5 py-1.5 rounded-lg border border-gray-200 hover:border-indigo-300">
              Export PDF
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default DriftDetectionModal;
